import { DestroyRef, ChangeDetectorRef, Component, ElementRef, Input, Output, EventEmitter, OnInit, OnChanges, SimpleChanges, ViewChild, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

import { catchError, debounceTime, retry, delay, takeUntil, finalize } from 'rxjs/operators';
import { of, Subject } from 'rxjs';
import { IconComponent } from '../../icon/icon.component';
import { WatchProgressChipComponent } from '../../watch-progress-chip/watch-progress-chip.component';
import { GradingRulesAlertComponent } from '../../grading-rules-alert/grading-rules-alert.component';
import { DebugService } from '../../../../shared/services/debug.service';
import { ProfileService } from '../../../../profile/profile.service';
import { QuizService, QuizSubmission, QuizAttempt, QuizAttemptQuestion } from '../../../../services/quiz.service';
import { VideoQuizSyncService } from '../../../../services/video-quiz-sync.service';
import { VideoProgressService } from '../../../../services/video-progress.service';
import { PlayerMode, PlayerConfig } from '../../../../services/course-player.service';
import { QuizQuestion } from '../split-screen-player.types';
import { FeedbackVisibility, FeedbackFlags } from '../../../../instructor/course-builder-dynamic/types/module-creation.types';
import { flagsFromScalar, scalarFromFlags, clampFlagsForPass, intersectFlags, isScoreOnly } from '../../../utils/feedback-flags.util';
import {
  gradeQuizForPreview,
  getQuestionPoints,
  getPerRowPoints,
  getWordCount,
  checkForManualGradingQuestions,
  matchAnswersToQuestions as matchAnswersToQuestionsFn,
  buildFeedbackFromBackendResults,
  isGridRowCorrect as isGridRowCorrectFn,
  isGridCellCorrectAnswer as isGridCellCorrectAnswerFn,
  isCheckboxGridRowCorrect as isCheckboxGridRowCorrectFn,
  isCheckboxCellCorrectAnswer as isCheckboxCellCorrectAnswerFn,
  isCheckboxCellUserSelected as isCheckboxCellUserSelectedFn
} from './quiz-grading.utils';
import { isQuestionAnswered } from './quiz-answered.utils';
import { carryAnswersForIdenticalQuestions, mergeSavedAnswersPreservingLocal } from './attempt-carry.utils';
import {
  isMcGridColumnChecked as isMcGridColumnCheckedFn,
  mcGridAcceptedColumns as mcGridAcceptedColumnsFn
} from '../../quiz-builder/utils/mc-grid-answer.utils';
import { QuestionStatusGridComponent } from './components/question-status-grid/question-status-grid.component';
import { isQuizPassing } from '@shared/utils/quiz-pass.utils';
import { formatScoreParts, ScoreDisplayParts } from '../../../utils/score-display.utils';
import { ScorePtsPipe } from '../../../pipes/score-pts.pipe';
import { LinkifyPipe, PlainTextPipe } from '../../../pipes/linkify.pipe';
import { ImageLightboxComponent } from '../../image-lightbox/image-lightbox.component';
// KAN-444: the single definition of "not finally graded yet", shared with the
// instructor's Quiz Submissions table so the two surfaces cannot drift apart.
import { isAwaitingGrading } from '../../../utils/grading-status.utils';

@Component({
  selector: 'app-quiz-panel',
  standalone: true,
  imports: [CommonModule, FormsModule, IconComponent, WatchProgressChipComponent, GradingRulesAlertComponent, ScorePtsPipe, LinkifyPipe, PlainTextPipe, QuestionStatusGridComponent, ImageLightboxComponent],
  templateUrl: './quiz-panel.component.html',
  styleUrls: ['./quiz-panel.component.scss']
})
export class QuizPanelComponent implements OnInit, OnChanges {
  private destroyRef = inject(DestroyRef);
  private cdr = inject(ChangeDetectorRef);
  public debug = inject(DebugService);

  // Quiz content inputs
  @Input() quizQuestions: QuizQuestion[] = [];
  /** Instructor-authored quiz description; rendered linkified so URLs are
   *  clickable for learners (KAN-224). */
  @Input() quizDescription: string = '';
  @Input() passingScore: number = 80;
  @Input() quizId?: string;
  @Input() moduleId?: string;
  @Input() courseId?: string;

  // Video state inputs (read-only from parent)
  @Input() isVideoRequired: boolean = true;
  @Input() videoCompleted: boolean = false;
  @Input() videoCompletedFromBackend: boolean = false;
  @Input() videoWatchPercentage: number = 0;
  @Input() requiredWatchPercentage: number = 90;
  @Input() videoEnded: boolean = false;
  @Input() videoId?: string;
  @Input() videoUrl?: string;

  // Player config inputs
  @Input() mode: PlayerMode = 'learner';
  @Input() currentPlayerConfig!: PlayerConfig;
  @Input() effectiveVideoRequirement: boolean = true;
  @Input() itemType: 'module' | 'unit_quiz' = 'module';

  // Feedback visibility level (KAN-69) — LEGACY scalar. Retained as a fallback
  // for hydration when the authoritative flags below are not supplied.
  @Input() feedbackVisibility: FeedbackVisibility = 'indicators_answers';

  /**
   * KAN-343: the three INDEPENDENT feedback flags — the AUTHORITATIVE gating
   * model. When supplied (new quizzes / arbitrary combos) they drive every
   * canShow* gate directly. When absent (legacy caller passing only the scalar
   * above), the flags are derived from `feedbackVisibility` via flagsFromScalar,
   * so pre-KAN-343 quizzes render identically.
   */
  @Input() feedbackFlags?: FeedbackFlags;

  /**
   * KAN-343: server-authoritative flags applied on top of the configured level
   * (KAN-219 clamp). Null until a server response (submission/progress) supplies
   * a value; once set, it is the base for effective-flag computation. Per-flag
   * AND against configured — the server can only ever RESTRICT, never relax.
   */
  private serverAppliedFlags: FeedbackFlags | null = null;

  /**
   * KAN-328: when true, correct answers + explanations are revealed ONLY if the
   * learner passed. On a FAIL the effective feedback level is clamped down to at
   * most `indicators` (see getEffectiveFeedbackVisibility) so answers cannot be
   * harvested by failing on purpose. Layers on top of feedbackVisibility; when
   * false (default) behavior is exactly the static feedbackVisibility behavior.
   *
   * On the server-scored learner path the backend already strips the answer
   * fields and sends a clamped feedbackVisibility (applyServerFeedbackVisibility)
   * — this input primarily makes the client-scored instructor preview faithful.
   */
  @Input() revealAnswersOnlyOnPass = false;

  /**
   * KAN-309: the quiz's CONFIGURED custom incorrect ("Note from instructor")
   * message. Distinct from the runtime `wrongAnswerFeedback` display field below:
   * on the server-scored path the gated text arrives in the submission response,
   * but instructor preview grades client-side (`useServerScoring=false`) and has
   * no response to source it from. This input lets the preview path apply the
   * same KAN-128 gate the backend does (failed + not score_only) so the failure
   * banner renders consistently across learner and instructor-preview surfaces.
   */
  @Input() customIncorrectMessage: string | null = null;

  /**
   * KAN-327: the quiz's CONFIGURED custom PASS ("success") message. The inverted
   * mirror of customIncorrectMessage: instructor preview grades client-side and
   * has no submission response, so this input lets the preview apply the same
   * pass-only gate the backend does (passed + not score_only) and render the
   * success banner consistently across the learner and instructor-preview surfaces.
   */
  @Input() customPassMessage: string | null = null;

  // UI state inputs
  @Input() isModal: boolean = false;
  @Input() canGoToPrevious: boolean = false;
  @Input() canGoToNext: boolean = false;
  /**
   * KAN-595: whether the learner is on the FINAL navigation item. Decides the
   * footer's completion LABEL, which used to read `!canGoToNext` as a proxy for
   * "last". That proxy is false for four different reasons - no next item, the
   * next item is sequentially LOCKED, the gate map has not loaded, or
   * courseStructure is null - and only the first means "last". On a sequential
   * course the button therefore announced "Complete Course" to a learner sitting
   * on module 1 of 3, and self-corrected only once module 2 unlocked.
   *
   * `canGoToNext` still decides whether Next is offered at all; nothing about
   * the gate changed. Only the label stopped lying about it.
   */
  @Input() isLastItem: boolean = false;
  /**
   * KAN-597: the module's completion is being saved and the server has not
   * answered. While true (and Next is not yet offered) the footer shows a
   * disabled "Saving progress..." instead of a completion button the learner
   * would click for nothing. Owned by the learner modal.
   */
  @Input() completionPending = false;
  @Input() moduleProgress?: any;
  @Input() useServerScoring = false;
  @Input() quizSubmissionError = false;
  /**
   * KAN-349: structured submit-error info (code/details) from the learner
   * modal's HTTP error handler, so attempt-lifecycle 409s (expired / already
   * submitted / draw failed) get dedicated UI instead of a generic alert.
   */
  @Input() quizSubmissionErrorInfo: { code?: string; message?: string; details?: any } | null = null;

  // KAN-577: the panel's scroll container. The learner modal keeps this same
  // component instance alive across a module -> module advance (its @if stays
  // true and only the inputs rebind), so this element's scrollTop survives the
  // content swap and lands the learner mid-quiz or at the bottom of the NEXT
  // module's quiz.
  @ViewChild('quizContent') quizContent?: ElementRef<HTMLElement>;

  // Outputs
  @Output() quizSubmit = new EventEmitter<{ answers: any[], score: number | null, passed: boolean | null, videoProgress?: { furthestPosition: number, duration: number }, attemptId?: string }>();
  @Output() quizRetry = new EventEmitter<void>();
  @Output() completeModule = new EventEmitter<void>();
  @Output() navigateNext = new EventEmitter<void>();
  @Output() navigatePrevious = new EventEmitter<void>();
  @Output() closePlayer = new EventEmitter<void>();
  @Output() progressUpdate = new EventEmitter<{
    moduleId: string;
    videoProgress: number;
    quizScore?: number;
    completed: boolean;
  }>();
  @Output() submissionRestored = new EventEmitter<{
    quizSubmitted: boolean;
    quizPassed: boolean;
    autoGradedScore: number;
    autoGradedMaxScore: number;
    isPartiallyGraded: boolean;
    guaranteedFail: boolean;
    gradingStatus: string | null;
  }>();

  // Quiz state
  userAnswers: any[] = [];
  quizSubmitted = false;
  quizSubmitting = false;
  quizSubmitEmitted = false;
  showFeedback = false;
  quizScore = 0;
  /**
   * KAN-127 follow-up: the canonical 1-dp percentage from the scoring engine
   * (the SAME value used for the pass/fail decision), captured from the
   * submission/progress payload's `percentage` field (Phase 6 backend). This is
   * what the result banner's anti-contradiction clamp must operate on — NOT the
   * integer `quizScore` (e.g. Math.round(79.5)=80), which would make the clamp a
   * no-op and render "(80%)" next to a Failed badge. Null when the payload omits
   * `percentage` (old cached responses) — the banner then falls back to quizScore.
   */
  quizPercentage: number | null = null;
  quizPassed = false;
  quizFeedback: any[] = [];
  wrongAnswerFeedback: string | null = null;
  // KAN-327: the gated custom PASS message currently displayed (success banner).
  passFeedback: string | null = null;
  currentQuestionIndex = 0;

  // Partial scoring for manual grading
  hasManualGradingQuestions = false;
  manualGradingPending = 0;
  autoGradedScore = 0;
  autoGradedMaxScore = 0;
  isPartiallyGraded = false;

  /**
   * Full quiz total (including manual-grade questions) captured at the moment
   * the submission was recorded. Drives the "N pts total" header so it stays
   * consistent with the "Your score: X/Y" badge even when the quiz has been
   * edited after the learner submitted (followup KAN-225).
   *
   * Distinct from `autoGradedMaxScore`, which on some paths (instructor live
   * preview) excludes manual-grade points and would shrink the header total
   * inappropriately.
   *
   * Null when no submission has been recorded yet — header falls back to
   * computing live from the current question array.
   */
  submittedTotalPoints: number | null = null;

  // Smart retry state for manual grading UX
  gradingStatus: string | null = null;
  guaranteedFail = false;
  manualPendingPoints = 0;
  bestPossiblePercentage = 0;

  // Submission pre-fill state
  submissionFetchRetryCount = 0;
  isViewingPreviousSubmission = false;
  submissionDate: string | null = null;
  // KAN-334: the viewed submission is a synthetic instructor grant (no real attempt).
  viewingManualGrant = false;
  // KAN-627: the viewed grant is prior completion credit -- shown as credit,
  // never as an earned score.
  viewingPriorCredit = false;
  showQuestionMismatchBanner = false;

  // KAN-314 VS1: attempt accounting from the server. Null = unlimited/unenforced.
  attemptLimit: number | null = null;
  attemptsRemaining: number | null = null;

  /** True when an attempt cap applies and the learner has used every attempt. */
  get noAttemptsRemaining(): boolean {
    return this.attemptLimit !== null && (this.attemptsRemaining ?? 0) <= 0;
  }

  /**
   * KAN-478: the learner holds a valid passing attempt for this quiz on the
   * server (non-invalidated, `passed = TRUE` — the same predicate migration 135
   * derives module/unit completion from). Stays false against a backend that
   * predates the field, which is why `quizEverPassed` also folds in the viewed
   * attempt's own verdict.
   */
  hasPassedAttempt = false;

  /**
   * KAN-478: "has this learner passed this quiz at all?", as distinct from
   * `quizPassed` = "did the attempt currently on screen pass?". The two split
   * the moment post-pass retakes became reachable: a learner can now sit on a
   * failed retake while their passing attempt still stands, drives completion,
   * and holds their certificate. Anything answering "may they move on / are they
   * stranded" must read THIS; anything describing the attempt in front of them
   * (score banner, pass/fail styling, feedback messages) must keep reading
   * `quizPassed`.
   */
  get quizEverPassed(): boolean {
    return this.hasPassedAttempt || this.quizPassed;
  }

  /**
   * KAN-388: the learner is genuinely STRANDED on a submitted quiz — they failed
   * it, every attempt is spent (Try Again is withheld), it is the LAST item in
   * the player (`!canGoToNext`, so there is no forward Next), and it is not
   * merely awaiting manual grading. In this state the modal footer's
   * Next/Complete block (gated on `quizPassed || isPartiallyGraded ||
   * !questions`) renders nothing, so without an explicit terminal verdict + exit
   * the learner dead-ends on the bare modal X with no pass/fail outcome shown
   * (parent KAN-322; the boundary case flagged in KAN-367's writeup).
   *
   * Scoped to `mode === 'learner'`: instructor preview/test have their own
   * always-present "Try Again" result and no real attempt cap. Display + close
   * ONLY — the terminal action reuses `closePlayer`; it NEVER completes the
   * module/course or issues a certificate (backend gating stands, KAN-362/364).
   *
   * KAN-478: reads `quizEverPassed`, not `quizPassed`. Post-pass retakes are now
   * reachable, so a learner can spend every attempt on retakes that score lower
   * than the pass they already hold — failed, exhausted, on the last item, and
   * NOT stranded at all. Their course is complete and their certificate issued.
   * (`!quizEverPassed` subsumes `!quizPassed`.)
   */
  get isStrandedFailure(): boolean {
    return this.isModal
      && this.mode === 'learner'
      && this.quizSubmitted
      && !this.quizEverPassed
      && !this.isPartiallyGraded
      && this.noAttemptsRemaining
      && !this.canGoToNext
      && this.quizQuestions.length > 0;
  }

  /**
   * KAN-348 review: quizzes that REQUIRE a server draw to grade correctly —
   * bank slots (authored stubs are unanswerable) or shuffled questions (an
   * index answer against the authored order can mis-grade). Plain fixed
   * quizzes grade identically with or without an attempt (identity mapping),
   * so only draw-requiring quizzes fail CLOSED when startAttempt errors.
   */
  get requiresServerDraw(): boolean {
    return (this.quizQuestions ?? []).some(
      (q: any) => q?.slotType === 'bank' || q?.shuffleOptions === true
    );
  }

  /**
   * KAN-414: true while quizQuestions still holds an UNRESOLVED bank slot
   * ({ slotType:'bank', bankId, pointsOverride } — no type/text/options). A slot
   * is not a question: rendering one produces a card with a points badge and an
   * empty body. The draw (learner attempt-start, or the instructor preview draw)
   * replaces every slot with its resolved question, so a slot still being present
   * means the render source is not yet showable.
   */
  get awaitingDraw(): boolean {
    return (this.quizQuestions ?? []).some((q: any) => q?.slotType === 'bank');
  }

  /**
   * Fail-closed gate: the attempt can no longer produce a valid submission —
   * deadline passed, attempt cap reached at start, or the start failed on a
   * quiz that needs the server draw.
   */
  get attemptSubmitBlocked(): boolean {
    return this.attemptExpired
      || this.attemptsExhaustedAtStart
      || (this.attemptStartFailed && this.requiresServerDraw);
  }

  /**
   * KAN-265 AC3 + REQ-QUIZ-110 (KAN-229): question-stem AND option images
   * (e.g. labeled lacrosse field diagrams used as MC/select-all options or as
   * Grid stems) carry small numbered markers and zone names a learner must read
   * to answer. Inline renders are enlarged, but to guarantee legibility on any
   * panel width we let the learner click any quiz image to open the SHARED
   * full-size lightbox (app-image-lightbox). Null = closed.
   */
  lightboxImageUrl: string | null = null;
  lightboxImageAlt = '';

  // KAN-348: learner attempt state. The open attempt's frozen question
  // snapshot (bank draws resolved, KAN-350 shuffle applied, variants
  // resolved) REPLACES quizQuestions as the rendering/grading source.
  // Supersedes the old start-session variant flow (REQ-QUIZ-301).
  attemptId: string | null = null;
  attemptLoadError: string | null = null;
  /** KAN-348 review: true after startAttempt failed (drives Retry + fail-closed submit). */
  attemptStartFailed = false;
  /**
   * KAN-332 QA Fix 4: true after startAttempt was rejected with 403
   * MODULE_LOCKED/UNIT_LOCKED. Distinct from attemptStartFailed - a lock is
   * not a recoverable failure to fail-open on; the authored questions are
   * actively cleared (see startOrResumeAttempt) so a locked learner never
   * sees quiz content the server refused to start an attempt for.
   */
  moduleLocked = false;
  moduleLockedReason: string | null = null;
  /**
   * KAN-345 (resume-clobber): true while a startAttempt POST is in flight. On
   * (re)entry the panel's start can be triggered more than once (quizId +
   * quizQuestions arriving in separate change-detection passes both cascade
   * into startOrResumeAttempt); the extra POST's late resumed response used to
   * re-apply savedAnswers and wipe a click made in the ~1s gap. This dedupes to
   * ONE attempt-start POST per entry. NOT cleared by resetQuizState (a
   * quizQuestions re-bind must not re-open the guard); cleared on genuine
   * quizId/placement change and on retake, which start a fresh attempt.
   */
  private attemptStartInFlight = false;

  // KAN-349: server-authoritative attempt deadline (attempt.expiresAt).
  attemptExpiresAt: string | null = null;
  /** Seconds until the attempt expires; null = no deadline. Signal so the
   *  interval tick re-renders under zoneless CD. */
  attemptRemainingSeconds = signal<number | null>(null);
  /**
   * KAN-469: how loudly the countdown should read, derived from the signal
   * above. The banner previously had two states - info, then alert-warning in
   * the final minute - which on a 45-minute exam is not a warning so much as
   * an announcement. `warning` opens a five-minute heads-up (the interval the
   * requirement asks for); `critical` keeps the existing final-minute state.
   * Purely presentational: expiry, submit blocking and the fresh-attempt
   * recovery are untouched.
   */
  readonly countdownUrgency = computed<'normal' | 'warning' | 'critical' | null>(() => {
    const remaining = this.attemptRemainingSeconds();
    if (remaining === null) return null;
    if (remaining < 60) return 'critical';
    if (remaining < 300) return 'warning';
    return 'normal';
  });
  private expiryTimer: ReturnType<typeof setInterval> | null = null;
  /** Deadline passed (local countdown) or the server 409'd ATTEMPT_EXPIRED. */
  attemptExpired = false;
  /** 409 ATTEMPT_LIMIT_REACHED on START — attempts exhausted before a new draw. */
  attemptsExhaustedAtStart = false;
  attemptsUsed: number | null = null;
  /** 409 ATTEMPT_DRAW_FAILED details.bankId (named in the error banner). */
  drawFailedBankId: string | null = null;
  /**
   * KAN-678: the open attempt was re-frozen because the instructor changed the
   * quiz after the learner opened it (start response `refreshed: true`, or a
   * stale-tab submit 409'd ATTEMPT_SUPERSEDED). Drives the "quiz was updated"
   * notice; cleared on retake and on a quiz/placement change.
   */
  attemptUpdatedNotice = false;
  /**
   * KAN-348 QA: true while quizQuestions were REPLACED by an attempt snapshot
   * (live attempt or Δ5 submission review). Snapshot questions carry the
   * EFFECTIVE points frozen on question.points (bank-slot overrides + grid
   * budget chain applied server-side) — the points display must use that for
   * every type instead of the authored KAN-241 mirrors (totalPoints), which
   * ride along stale (QA scenario 4: override=3 select-all showed "Worth 1").
   */
  questionsFromSnapshot = false;

  /** @implements REQ-GRADE-203 - Store grading metadata from backend for grid question correctness */
  questionResults: any[] | null = null;

  /** Cancels in-flight submission fetches when quizId changes (KAN-120) */
  private fetchCancel$ = new Subject<void>();

  /** KAN-348 Δ3: debounced autosave trigger — every answer mutation queues a
   *  best-effort PUT of the in-progress answers onto the open attempt. */
  private autosaveTrigger$ = new Subject<void>();

  /** Display order indices for option-based questions (MC, Select All).
   *  For each question index, holds an array of original option indices in display order.
   *  e.g. optionDisplayOrder[2] = [3, 0, 1, 2] means question 2 shows option 3 first. */
  optionDisplayOrder: number[][] = [];

  /**
   * KAN-127 Phase 1: compute score display parts for the result banner.
   *
   * Sources the percentage from the canonical `moduleProgress.percentage` field
   * (Phase 6 backend) when available; falls back to `quizScore` (the INTEGER
   * rounded percentage). The anti-contradiction clamp in `formatScoreParts` ensures
   * the displayed number can never visually contradict the Passed/Failed badge.
   *
   * Gated on `autoGradedMaxScore > 0` — this is the D5 guard that prevents
   * rendering `(0%)` before the quiz has been scored. `quizScore` is typed
   * `number` and defaults to 0, so `quizScore != null` is always true and
   * cannot serve as a gate.
   */
  get resultBannerScoreParts(): ScoreDisplayParts | null {
    if (this.autoGradedMaxScore <= 0) {
      return null;
    }
    // Build a minimal data shape that formatScoreParts understands.
    // KAN-127 follow-up: prefer the canonical 1-dp percentage captured from the
    // submission/progress result (`quizPercentage`) — it is populated on the
    // just-submitted/saved/prefill learner paths where `moduleProgress.percentage`
    // is absent. Without this the clamp operated on the integer quizScore and was a
    // no-op, rendering "(80%) Failed" for a 79.5% score. Order: captured canonical →
    // moduleProgress.percentage (reload path) → null (fall back to integer quizScore).
    const percentage = this.quizPercentage ?? this.moduleProgress?.percentage ?? null;
    const data = {
      quizEarnedPoints: this.autoGradedScore,
      quizTotalPoints: this.autoGradedMaxScore,
      quizScore: this.quizScore,
      percentage,
      passed: this.quizPassed,
    };
    return formatScoreParts(data, this.passingScore);
  }

  constructor(
    private profileService: ProfileService,
    private quizService: QuizService,
    private syncService: VideoQuizSyncService,
    private videoProgressService: VideoProgressService
  ) {}

  ngOnInit(): void {
    // KAN-349: never leak the expiry interval past the component.
    this.destroyRef.onDestroy(() => this.clearExpiryCountdown());

    // KAN-348 Δ3: debounced autosave of in-progress answers (~2s after the
    // last change). Silent best-effort — a failed PUT is simply retried by
    // the next debounce; resume restores via attempt.savedAnswers.
    this.autosaveTrigger$.pipe(
      debounceTime(2000),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(() => this.flushAutosave());

    // Best-effort flush when the tab is hidden (navigation/close) so the
    // latest answers survive without waiting out the debounce.
    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        this.flushAutosave();
      }
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    this.destroyRef.onDestroy(() => document.removeEventListener('visibilitychange', onVisibilityChange));

    // Initialize empty answers array
    this.userAnswers = new Array(this.quizQuestions.length).fill(null);

    // Compute display order for option-based questions
    this.computeDisplayOrder();

    // Check for manual grading questions
    this.checkForManualGradingQuestions();

    // Initialize sync service
    this.syncService.updateQuizState({
      totalQuestions: this.quizQuestions.length,
      isLocked: this.isVideoRequired
    });
  }

  /**
   * KAN-239 (reopened): Derive the authoritative pass/fail verdict from a
   * server-scored progress entry. `progress.quizScore` is the backend's
   * INTEGER-rounded `percentageScore` (e.g. Math.round(79.5) = 80), so it must
   * NOT be used to decide pass/fail. Raw earned/total points are ground truth —
   * they reproduce the exact computeQuizResult boundary — so when present we
   * recompute from them and IGNORE any stored `quizPassed` boolean, keeping this
   * footer verdict identical to the under-video badge even if a stale/incorrect
   * boolean is carried on the progress entry. Precedence:
   *   1. raw `quizEarnedPoints / quizTotalPoints` -> isQuizPassing
   *   2. authoritative `quizPassed` boolean (only when raw points are absent —
   *      the instructor-preview path)
   *   3. last resort: the integer percentage via isQuizPassing
   */
  private deriveServerPassed(progress: any): boolean {
    if (progress?.quizEarnedPoints != null && progress?.quizTotalPoints > 0) {
      return isQuizPassing(
        (progress.quizEarnedPoints / progress.quizTotalPoints) * 100,
        this.passingScore
      );
    }
    if (typeof progress?.quizPassed === 'boolean') {
      return progress.quizPassed;
    }
    return isQuizPassing(progress?.quizScore, this.passingScore);
  }

  ngOnChanges(changes: SimpleChanges): void {
    // If quiz questions changed, reset quiz state
    if (changes['quizQuestions'] && !changes['quizQuestions'].firstChange) {
      console.log('[QuizPanel] Quiz questions changed, resetting quiz state');
      this.resetQuizState();
      this.computeDisplayOrder();
    }

    // If quizId changed (e.g., navigating between modules, or after progress reset),
    // reset stale state and re-fetch submission data from backend (KAN-120)
    const quizIdChanged = changes['quizId'] &&
      !changes['quizId'].firstChange &&
      changes['quizId'].currentValue !== changes['quizId'].previousValue;

    // KAN-270: navigating between two placements of the SAME reused quiz asset
    // does NOT change quizId (same asset), so a moduleId/itemType change must
    // also reset + re-fetch — otherwise the new placement keeps the prior
    // placement's "Viewing your submission from..." state and pre-filled answers.
    const placementChanged =
      (changes['moduleId'] &&
        !changes['moduleId'].firstChange &&
        changes['moduleId'].currentValue !== changes['moduleId'].previousValue) ||
      (changes['itemType'] &&
        !changes['itemType'].firstChange &&
        changes['itemType'].currentValue !== changes['itemType'].previousValue);

    // KAN-577 A: a new quiz/placement must start at the TOP. The same DOM
    // element is reused across the swap, so without this the learner keeps the
    // previous module's offset. Written synchronously rather than deferred:
    // 0 is in range for any scrollHeight, so it does not depend on the new
    // content having rendered (the trap a measured scroll target falls into
    // under zoneless change detection).
    if (quizIdChanged || placementChanged) {
      this.resetPanelScroll();
    }

    let fetchTriggeredByQuizIdChange = false;
    if (quizIdChanged || placementChanged) {
      // KAN-345: a genuine new quiz/placement starts a fresh attempt, so re-open
      // the dedupe guard even if a prior start is still mid-flight. (A
      // quizQuestions-only re-bind does NOT clear it — see attemptStartInFlight.)
      this.attemptStartInFlight = false;
      this.resetQuizState();
      this.computeDisplayOrder();
      this.fetchAndPrefillSubmission();
      fetchTriggeredByQuizIdChange = true;
    }

    // Fetch and pre-fill when quiz questions are loaded (skip if quizId change already triggered fetch)
    if (!fetchTriggeredByQuizIdChange &&
        changes['quizQuestions'] &&
        this.quizQuestions &&
        this.quizQuestions.length > 0 &&
        this.quizId &&
        !this.isViewingPreviousSubmission) {
      this.fetchAndPrefillSubmission();
    }

    // Handle quiz submission error: reset spinner so user can retry
    if (changes['quizSubmissionError'] && changes['quizSubmissionError'].currentValue === true) {
      console.log('[QuizPanel] Quiz submission error received, resetting submitting state');
      this.quizSubmitting = false;
      this.quizSubmitEmitted = false;
    }

    // KAN-349: attempt-lifecycle 409s get dedicated recovery UI.
    // KAN-678: never on the FIRST change. The learner modal keeps the last
    // submit error until the next submit, and this panel is recreated when
    // the learner leaves the quiz item and comes back, so a first value is a
    // stale error from a destroyed panel's submit (replaying it re-raised the
    // "quiz was updated" notice / expired banner and restarted the attempt).
    const submitErrorInfo = changes['quizSubmissionErrorInfo'];
    if (submitErrorInfo && submitErrorInfo.currentValue && !submitErrorInfo.firstChange) {
      this.handleSubmitErrorInfo(submitErrorInfo.currentValue);
    }

    // Update quiz display when backend scores arrive via moduleProgress
    if (changes['moduleProgress'] && !changes['moduleProgress'].firstChange) {
      const progress = changes['moduleProgress'].currentValue;

      // Server scores arrived while we were waiting (server-authoritative mode)
      if (progress && this.quizSubmitting && this.quizSubmitEmitted) {
        this.quizSubmitEmitted = false;
        this.quizSubmitting = false;
        this.quizSubmitted = true;
        this.showFeedback = true;

        // KAN-343/KAN-219: apply the submission-response feedback flags, but
        // never relax below the configured level. Falls back to the legacy
        // scalar when the response predates flags.
        this.applyServerFeedbackFlags(progress.feedbackFlags ?? progress.feedbackVisibility);

        if (progress.quizScore !== null && progress.quizScore !== undefined) {
          // KAN-239: quizScore is the backend's INTEGER-rounded percentage — use
          // it for DISPLAY only. Pass/fail comes from the authoritative `quizPassed`
          // boolean (or raw earned/total points), never the rounded display value.
          this.quizScore = Math.round(progress.quizScore);
          this.quizPassed = this.deriveServerPassed(progress);
        }

        // KAN-127 follow-up: capture the canonical 1-dp percentage (Phase 6 backend)
        // so the banner clamp can show the true value (e.g. 79.5%) instead of the
        // integer quizScore (80) that would contradict the Failed badge.
        if (progress.percentage !== null && progress.percentage !== undefined) {
          this.quizPercentage = progress.percentage;
        }

        if (progress.quizEarnedPoints !== null && progress.quizEarnedPoints !== undefined) {
          this.autoGradedScore = progress.quizEarnedPoints;
          this.autoGradedMaxScore = progress.quizTotalPoints;
          // Followup KAN-225: track stored quiz total so the header denominator
          // matches the score badge even when the quiz was edited post-submit.
          this.submittedTotalPoints = progress.quizTotalPoints;
        }

        if (progress.questionResults && Array.isArray(progress.questionResults)) {
          this.questionResults = progress.questionResults;
          this.updateQuizFeedbackFromBackend(progress.questionResults);
        }

        // Capture wrong answer feedback from backend
        if (progress.wrongAnswerFeedback) {
          this.wrongAnswerFeedback = progress.wrongAnswerFeedback;
        }

        // KAN-327: capture the gated custom pass message from the backend (the
        // service already gates it to a PASS with non-score_only visibility).
        if (progress.passFeedback) {
          this.passFeedback = progress.passFeedback;
        }

        // Detect manual grading pending state
        if (isAwaitingGrading(progress.gradingStatus)) {
          this.isPartiallyGraded = true;
          this.guaranteedFail = progress.guaranteedFail ?? false;
          this.manualPendingPoints = progress.manualPendingPoints ?? 0;
          this.bestPossiblePercentage = progress.bestPossiblePercentage ?? 0;
          this.gradingStatus = progress.gradingStatus ?? null;
          this.quizPassed = false;
        }

        // KAN-314 VS1: capture attempt accounting from the live submit so the
        // result view withholds "Try Again" the moment the cap is reached.
        if (progress.attemptLimit !== undefined) {
          this.attemptLimit = progress.attemptLimit ?? null;
          this.attemptsRemaining = progress.attemptsRemaining ?? null;
        }

        this.syncService.updateQuizState({ isSubmitted: true, score: this.quizScore });
      }

      // Existing behavior: update scores after quiz already submitted (e.g., server correction)
      if (progress && this.quizSubmitted && !this.quizSubmitting) {
        console.log('[QuizPanel] Backend scores received, updating quiz display');

        // KAN-207: Detect aggregate-only progress reloads that would clobber
        // the live submission's authoritative display. The course-progress
        // endpoint (/progress/user/.../course/...) returns the BEST historical
        // submission's aggregate (`ORDER BY qs.score DESC LIMIT 1`) WITHOUT
        // per-question results. When the just-submitted attempt scored lower
        // than a previous attempt, this reload arrives with an inflated
        // `quizEarnedPoints` that doesn't match the per-question feedback
        // already on screen. Skip those clobbering updates — the only
        // legitimate post-submit updates (manual-grading completion, instructor
        // overrides) include `questionResults` alongside the aggregate.
        const hasQuestionResults =
          Array.isArray(progress.questionResults) && progress.questionResults.length > 0;
        const haveLiveDisplay =
          Array.isArray(this.questionResults) && this.questionResults.length > 0;
        const isStaleAggregateOnly = haveLiveDisplay && !hasQuestionResults;

        // KAN-343/KAN-219: clamp to the configured level — never relax (see above).
        this.applyServerFeedbackFlags(progress.feedbackFlags ?? progress.feedbackVisibility);

        if (
          !isStaleAggregateOnly &&
          progress.quizScore !== null && progress.quizScore !== undefined
        ) {
          // KAN-239: quizScore is the backend's INTEGER-rounded percentage — use
          // it for DISPLAY only. Pass/fail comes from the authoritative `quizPassed`
          // boolean (or raw earned/total points), never the rounded display value.
          this.quizScore = Math.round(progress.quizScore);
          this.quizPassed = this.deriveServerPassed(progress);
        }

        // KAN-127 follow-up: capture the canonical 1-dp percentage (Phase 6 backend)
        // for the banner clamp. Gated by the same stale-aggregate guard so a
        // clobbering reload (KAN-207) doesn't replace the live submission's value.
        if (
          !isStaleAggregateOnly &&
          progress.percentage !== null && progress.percentage !== undefined
        ) {
          this.quizPercentage = progress.percentage;
        }

        if (
          !isStaleAggregateOnly &&
          progress.quizEarnedPoints !== null && progress.quizEarnedPoints !== undefined &&
          progress.quizTotalPoints !== null && progress.quizTotalPoints !== undefined
        ) {
          this.autoGradedScore = progress.quizEarnedPoints;
          this.autoGradedMaxScore = progress.quizTotalPoints;
          // Followup KAN-225: track stored total for header consistency.
          this.submittedTotalPoints = progress.quizTotalPoints;
        }

        if (hasQuestionResults) {
          this.questionResults = progress.questionResults;
          this.updateQuizFeedbackFromBackend(progress.questionResults);
        }

        // Capture wrong answer feedback from backend
        if (progress.wrongAnswerFeedback) {
          this.wrongAnswerFeedback = progress.wrongAnswerFeedback;
        }

        // KAN-327: capture the gated custom pass message from the backend (the
        // service already gates it to a PASS with non-score_only visibility).
        if (progress.passFeedback) {
          this.passFeedback = progress.passFeedback;
        }

        // Detect manual grading pending state
        if (isAwaitingGrading(progress.gradingStatus)) {
          this.isPartiallyGraded = true;
          this.guaranteedFail = progress.guaranteedFail ?? false;
          this.manualPendingPoints = progress.manualPendingPoints ?? 0;
          this.bestPossiblePercentage = progress.bestPossiblePercentage ?? 0;
          this.gradingStatus = progress.gradingStatus ?? null;
          this.quizPassed = false;
        }
      }
    }
  }
  // ─── Quiz interaction methods ─────────────────────────────────────

  selectAnswer(questionIndex: number, answerValue: any): void {
    if (this.quizSubmitted) return;

    const question = this.quizQuestions[questionIndex];

    if (question.type === 'select-all') {
      if (!this.userAnswers[questionIndex]) {
        this.userAnswers[questionIndex] = [];
      }
      const index = this.userAnswers[questionIndex].indexOf(answerValue);
      if (index > -1) {
        this.userAnswers[questionIndex].splice(index, 1);
      } else {
        this.userAnswers[questionIndex].push(answerValue);
      }
    } else {
      this.userAnswers[questionIndex] = answerValue;
    }

    this.queueAutosave();
  }

  isAnswerSelected(questionIndex: number, optionIndex: number): boolean {
    const answer = this.userAnswers[questionIndex];
    const question = this.quizQuestions[questionIndex];

    if (question.type === 'select-all') {
      return Array.isArray(answer) && answer.includes(optionIndex);
    }
    return answer === optionIndex;
  }

  isSelectAllOptionCorrect(questionIndex: number, optionIndex: number): boolean {
    const question = this.quizQuestions[questionIndex];
    if (!question || question.type !== 'select-all') return false;

    // Server-scored learner mode: question.answer is stripped. Prefer
    // correctAnswerRaw (raw index array) over correctAnswer, which the backend
    // resolves to a joined display string (KAN-178).
    const result = this.questionResults?.[questionIndex];
    const rawFromResult = result?.correctAnswerRaw
      ?? (Array.isArray(result?.correctAnswer) ? result.correctAnswer : null);
    if (Array.isArray(rawFromResult)) {
      const firstElement = rawFromResult[0];
      if (typeof firstElement === 'boolean') {
        return rawFromResult[optionIndex] === true;
      } else {
        return rawFromResult.includes(optionIndex);
      }
    }

    // Fallback for instructor preview mode where question.answer is available
    if (Array.isArray(question.answer)) {
      const firstElement = question.answer[0];
      if (typeof firstElement === 'boolean') {
        return question.answer[optionIndex] === true;
      } else {
        return question.answer.includes(optionIndex);
      }
    }

    return false;
  }

  /**
   * KAN-274: MC-grid is now MULTI-SELECT. The learner can check 0, 1, or many
   * columns per row, so a row's answer is stored as a boolean[] (one entry per
   * column) — the SAME shape checkbox-grid uses — and graded by exact-match
   * against the accepted-column set. Toggling a checkbox flips that column.
   *
   * A no-answer row stays correct simply by leaving every column unchecked
   * (no special sentinel needed — an all-false row is "nothing checked").
   */
  selectMcGridCheckbox(questionIndex: number, rowIndex: number, colIndex: number): void {
    if (this.quizSubmitted) return;

    const question = this.quizQuestions[questionIndex];
    if (!question || question.type !== 'multiple-choice-grid') return;

    this.toggleGridCell(questionIndex, rowIndex, colIndex, question.gridColumns?.length || 0);
  }

  selectGridCheckbox(questionIndex: number, rowIndex: number, colIndex: number): void {
    if (this.quizSubmitted) return;

    const question = this.quizQuestions[questionIndex];
    if (!question || question.type !== 'checkbox-grid') return;

    this.toggleGridCell(questionIndex, rowIndex, colIndex, question.gridColumns?.length || 0);
  }

  /**
   * KAN-274: shared boolean[]-per-row toggle for both grid types. Ensures the
   * row is a boolean[] sized to the column count (migrating a legacy single-int
   * or short row), then flips the target cell.
   */
  private toggleGridCell(questionIndex: number, rowIndex: number, colIndex: number, numColumns: number): void {
    if (numColumns === 0) return;

    if (!this.userAnswers[questionIndex]) {
      this.userAnswers[questionIndex] = [];
    }

    const existing = this.userAnswers[questionIndex][rowIndex];
    if (!Array.isArray(existing) || existing.length !== numColumns) {
      const fresh = new Array(numColumns).fill(false);
      if (Array.isArray(existing)) {
        for (let i = 0; i < Math.min(existing.length, numColumns); i++) {
          fresh[i] = existing[i] === true;
        }
      } else if (typeof existing === 'number' && existing >= 0 && existing < numColumns) {
        // Migrate a legacy single-int pick to the boolean[] shape.
        fresh[existing] = true;
      }
      this.userAnswers[questionIndex][rowIndex] = fresh;
    }

    const row = this.userAnswers[questionIndex][rowIndex];
    row[colIndex] = !row[colIndex];

    this.queueAutosave();
  }

  /** KAN-274: true when the learner has checked this MC-grid cell. */
  isMcGridCellChecked(questionIndex: number, rowIndex: number, colIndex: number): boolean {
    return isMcGridColumnCheckedFn(this.userAnswers[questionIndex]?.[rowIndex], colIndex);
  }

  /**
   * KAN-333: record the learner's picked right-item index for a matching
   * question's left item. The answer is stored as a number[] aligned to the
   * left items (userAnswers[qi][leftIndex] = matchRight index). An empty
   * selection ('') resets the pair to null (unmatched).
   */
  selectMatchingAnswer(questionIndex: number, leftIndex: number, event: Event): void {
    if (this.quizSubmitted) return;

    const question = this.quizQuestions[questionIndex];
    if (!question || question.type !== 'matching') return;

    if (!Array.isArray(this.userAnswers[questionIndex])) {
      const leftCount = question.matchLeft?.length || 0;
      this.userAnswers[questionIndex] = new Array(leftCount).fill(null);
    }

    const raw = (event.target as HTMLSelectElement).value;
    this.userAnswers[questionIndex][leftIndex] = raw === '' ? null : Number(raw);

    this.queueAutosave();
  }

  /** KAN-333: the learner's currently picked right index for a matching pair (or '' when unmatched). */
  matchingAnswerFor(questionIndex: number, leftIndex: number): number | '' {
    const value = this.userAnswers[questionIndex]?.[leftIndex];
    return typeof value === 'number' ? value : '';
  }

  /**
   * KAN-333: the correct right index for a matching pair, resolved from the
   * server result (correctAnswerRaw) in learner mode or the question answer in
   * instructor-preview mode. Drives the review-state "correct answer" hint.
   */
  matchingCorrectIndexFor(questionIndex: number, leftIndex: number): number | null {
    const result = this.questionResults?.[questionIndex];
    const raw = result?.correctAnswerRaw
      ?? (Array.isArray(result?.correctAnswer) ? result?.correctAnswer : null)
      ?? (Array.isArray(this.quizQuestions[questionIndex]?.answer) ? this.quizQuestions[questionIndex].answer : null);
    if (Array.isArray(raw) && typeof raw[leftIndex] === 'number') {
      return raw[leftIndex];
    }
    return null;
  }

  /** KAN-333: true when the learner paired this matching row correctly (review state). */
  isMatchingPairCorrect(questionIndex: number, leftIndex: number): boolean {
    const correct = this.matchingCorrectIndexFor(questionIndex, leftIndex);
    const picked = this.userAnswers[questionIndex]?.[leftIndex];
    return correct !== null && typeof picked === 'number' && picked === correct;
  }

  /** KAN-333: right-item label for a matching question by index (for review display). */
  matchingRightLabel(questionIndex: number, rightIndex: number | null): string {
    if (rightIndex === null || rightIndex === undefined) return '';
    const right = this.quizQuestions[questionIndex]?.matchRight?.[rightIndex];
    return right?.label ?? '';
  }

  /** KAN-333: the label the learner picked for a matching pair (review state), or '' if unmatched. */
  matchingLearnerLabel(questionIndex: number, leftIndex: number): string {
    const picked = this.userAnswers[questionIndex]?.[leftIndex];
    return typeof picked === 'number' ? this.matchingRightLabel(questionIndex, picked) : '';
  }

  onParagraphInput(questionIndex: number, event: any): void {
    const value = event.target.value;
    const question = this.quizQuestions[questionIndex];

    if (question.wordLimit) {
      const wordCount = this.getWordCount(value);
      if (wordCount > question.wordLimit) {
        const words = value.trim().split(/\s+/);
        const trimmed = words.slice(0, question.wordLimit).join(' ');
        this.userAnswers[questionIndex] = trimmed;
        event.target.value = trimmed;
      }
    }

    this.queueAutosave();
  }

  /** KAN-577 A: park the panel back at the top of the new module's quiz. */
  private resetPanelScroll(): void {
    const el = this.quizContent?.nativeElement;
    if (el) {
      el.scrollTop = 0;
    }
  }

  scrollToQuestion(index: number): void {
    this.currentQuestionIndex = index;
    const element = document.getElementById(`question-${index}`);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }

  /**
   * Stable index-based trackBy for every *ngFor in the template
   * (questions, options, grid rows/cols). Questions/options/grid arrays
   * are addressed positionally (answer indices reference position), so
   * trackBy:index matches the existing data contract and prevents
   * DOM teardown on parent re-renders.
   */
  trackByIndex(index: number, _item: unknown): number {
    return index;
  }

  // ─── KAN-265 / KAN-229: quiz image lightbox (stem + option images) ──

  /**
   * Open the shared full-size lightbox for any quiz image (stem or option).
   * Focus management + Escape/backdrop dismissal are owned by app-image-lightbox.
   */
  openLightbox(url: string | null | undefined, alt: string | null | undefined): void {
    if (!url) return;
    this.lightboxImageUrl = url;
    this.lightboxImageAlt = alt || '';
  }

  /** Dismiss the lightbox (backdrop click, close button, or Escape). */
  closeLightbox(): void {
    this.lightboxImageUrl = null;
    this.lightboxImageAlt = '';
  }

  /** Activate a quiz image (stem or option) with keyboard (Enter / Space). */
  onStemImageKeydown(event: KeyboardEvent, url: string | null | undefined, alt: string | null | undefined): void {
    if (event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar') {
      event.preventDefault();
      this.openLightbox(url, alt);
    }
  }

  // ─── Grid feedback methods (delegates to pure functions) ─────────

  canShowGridFeedback(questionIndex: number): boolean {
    // KAN-343: per-cell grid correct-answer reveal is the `showAnswers` element.
    // (The old util gated it on the scalar being >= indicators_answers.)
    if (!this.canShowAnswers()) return false;
    const question = this.quizQuestions[questionIndex];
    const result = this.questionResults?.[questionIndex] ?? null;
    return !!result || !!question?.answer;
  }

  /** Returns the effective feedback visibility level.
   *
   *  KAN-219 (fix): previously this short-circuited to
   *  'indicators_answers_explanations' whenever `useServerScoring` was false
   *  (instructor preview). That hardcoded behavior made the Feedback
   *  Visibility setting invisible in Quiz Preview, so admins had no way to
   *  actually verify what their learners would see.
   *
   *  We now always honor the configured `feedbackVisibility`. The instructor
   *  preview already runs with full client-side grading data available, so
   *  applying the same UI gates as a learner produces an accurate preview
   *  of the learner experience without leaking anything sensitive (preview
   *  data is in-memory only — there is no network response to filter).
   */
  /**
   * KAN-343: the CONFIGURED feedback flags (before the reveal-on-pass clamp).
   * Prefers the authoritative `feedbackFlags` @Input; falls back to deriving
   * from the legacy `feedbackVisibility` scalar so pre-KAN-343 quizzes render
   * identically. Once a server response has supplied stricter flags (KAN-219
   * clamp via applyServerFeedbackFlags) those become the base.
   */
  private getConfiguredFlags(): FeedbackFlags {
    if (this.serverAppliedFlags) return this.serverAppliedFlags;
    return this.feedbackFlags ?? flagsFromScalar(this.feedbackVisibility);
  }

  /**
   * KAN-343: the EFFECTIVE flags the learner may actually see, after the
   * KAN-328 reveal-on-pass clamp. On a FAIL with reveal-on-pass enabled,
   * answers + explanations are withheld (an explanation can leak the answer);
   * indicators + score survive.
   */
  getEffectiveFeedbackFlags(): FeedbackFlags {
    return clampFlagsForPass(this.getConfiguredFlags(), {
      revealAnswersOnlyOnPass: this.revealAnswersOnlyOnPass,
      passed: this.quizPassed
    });
  }

  /**
   * Back-compat shim (KAN-343): the effective level expressed as the legacy
   * nested scalar. Retained for any external caller/spec; internal gating now
   * reads the flags directly via canShow*.
   */
  getEffectiveFeedbackVisibility(): FeedbackVisibility {
    return scalarFromFlags(this.getEffectiveFeedbackFlags());
  }

  /**
   * KAN-219 (reopen #5) over flags: apply a server-supplied feedback level to
   * the panel WITHOUT ever relaxing below the configured level. The server is
   * authoritative and may only make feedback STRICTER (per-flag AND). This is
   * the defense-in-depth guard so no future omission can relax a locked-down
   * quiz. Accepts either a `feedbackFlags` object or the legacy scalar (from
   * which flags are derived) for backward compatibility.
   */
  private applyServerFeedbackFlags(value: FeedbackFlags | FeedbackVisibility | any): void {
    if (!value) return;
    const incoming: FeedbackFlags = typeof value === 'string'
      ? flagsFromScalar(value)
      : value as FeedbackFlags;
    const configured = this.feedbackFlags ?? flagsFromScalar(this.feedbackVisibility);
    this.serverAppliedFlags = intersectFlags(configured, incoming);
  }

  /** Whether to show right/wrong indicators per question. */
  canShowIndicators(): boolean {
    return this.getEffectiveFeedbackFlags().showIndicators;
  }

  /** Whether to reveal correct answers. */
  canShowAnswers(): boolean {
    return this.getEffectiveFeedbackFlags().showAnswers;
  }

  /** Whether to show per-question explanation text. */
  canShowExplanations(): boolean {
    return this.getEffectiveFeedbackFlags().showExplanations;
  }

  /**
   * KAN-254: instructor-authored explanation texts for a question, revealed
   * ONLY at the indicators_answers_explanations (Full Feedback) tier.
   *
   * Mirrors backend getQuestionFeedback semantics: the correctness-specific
   * message (correct/incorrect) first, then the general message. The
   * wrong-answer explanation is suppressed while a question is still awaiting
   * manual grading — there is no verdict to explain yet.
   */
  getExplanationTexts(questionIndex: number): string[] {
    if (!this.canShowExplanations()) return [];
    const item = this.quizFeedback[questionIndex];
    const fb = item?.feedback;
    if (!fb) return [];
    const texts: string[] = [];
    if (!item.requiresManualGrading) {
      const specific = item.isCorrect ? fb.correct : fb.incorrect;
      if (specific) texts.push(specific);
    }
    if (fb.general) texts.push(fb.general);
    return texts;
  }

  /** Whether grid row right/wrong highlighting should show (indicators level and above).
   *  This is separate from canShowGridFeedback which also gates correct answer reveal. */
  canShowGridRowIndicators(questionIndex: number): boolean {
    if (!this.canShowIndicators()) return false;
    const question = this.quizQuestions[questionIndex];
    const result = this.questionResults?.[questionIndex] ?? null;
    if (result) return true;
    if (question.answer) return true;
    return false;
  }

  isGridRowCorrect(questionIndex: number, rowIndex: number): boolean {
    return isGridRowCorrectFn(this.quizQuestions[questionIndex], this.userAnswers[questionIndex], this.questionResults?.[questionIndex] ?? null, rowIndex);
  }

  isGridCellCorrectAnswer(questionIndex: number, rowIndex: number, colIndex: number): boolean {
    return isGridCellCorrectAnswerFn(this.quizQuestions[questionIndex], rowIndex, colIndex, this.questionResults?.[questionIndex] ?? null);
  }

  isCheckboxGridRowCorrect(questionIndex: number, rowIndex: number): boolean {
    return isCheckboxGridRowCorrectFn(this.quizQuestions[questionIndex], this.userAnswers[questionIndex], this.questionResults?.[questionIndex] ?? null, rowIndex);
  }

  isCheckboxCellCorrectAnswer(questionIndex: number, rowIndex: number, colIndex: number): boolean {
    return isCheckboxCellCorrectAnswerFn(this.quizQuestions[questionIndex], rowIndex, colIndex, this.questionResults?.[questionIndex] ?? null);
  }

  isCheckboxCellUserSelected(questionIndex: number, rowIndex: number, colIndex: number): boolean {
    return isCheckboxCellUserSelectedFn(this.userAnswers[questionIndex], rowIndex, colIndex);
  }

  // ─── Quiz submission and grading ──────────────────────────────────

  allQuestionsAnswered(): boolean {
    return this.quizQuestions.every((_, index) => this.isAnsweredForSubmit(index));
  }

  /** KAN-77: per-question answered flag for the pre-submit status grid.
   *
   *  KAN-526: this asks "did the learner answer this?" and must NOT borrow the
   *  submit gate's optional-field exemption. It used to delegate to
   *  `isAnsweredForSubmit`, which reports every information-collection question
   *  as answered whether or not anything was typed - so a visibly empty field
   *  carried a checkmark in the review checklist and was counted in the
   *  "N of M answered" total. */
  isQuestionAnswered(questionIndex: number): boolean {
    return isQuestionAnswered(this.userAnswers[questionIndex]);
  }

  /** KAN-436: an information-collection field is optional/record-only. An
   * untouched null is a valid empty response and must not block submission.
   *
   * This is the SUBMIT GATE only. It deliberately reports more questions as
   * complete than {@link isQuestionAnswered} does; the difference is exactly
   * the set of optional questions the learner may leave blank. */
  private isAnsweredForSubmit(questionIndex: number): boolean {
    return this.quizQuestions[questionIndex]?.type === 'information-collection'
      || isQuestionAnswered(this.userAnswers[questionIndex]);
  }

  private _answeredFlags: boolean[] = [];
  private _answeredFlagsKey = '';

  /** KAN-77: answered flags (one per question) feeding the status grid.
   *  Memoized so it returns a stable array reference when the answered-state
   *  hasn't changed — keeps the OnPush grid from re-rendering every CD and
   *  avoids a test-only ExpressionChangedAfterChecked under zoneless CD. */
  get questionAnsweredFlags(): boolean[] {
    // KAN-526: the grid reports what the learner actually answered, not what
    // the submit gate is willing to let through.
    const flags = this.quizQuestions.map((_, i) => this.isQuestionAnswered(i));
    const key = flags.map(f => (f ? '1' : '0')).join('');
    if (key !== this._answeredFlagsKey) {
      this._answeredFlagsKey = key;
      this._answeredFlags = flags;
    }
    return this._answeredFlags;
  }

  canSubmitQuiz(): boolean {
    // KAN-348 review: no attempt could be started for a quiz that requires a
    // server draw (bank slots / shuffle) — submitting would grade the wrong
    // thing. Fail CLOSED; the Retry button re-calls startAttempt.
    if (this.attemptSubmitBlocked) {
      return false;
    }

    const allQuestionsAnswered = this.allQuestionsAnswered();

    // End-of-unit quizzes are not associated with a single module video, so
    // they are never video-gated.
    if (this.itemType === 'unit_quiz') {
      return allQuestionsAnswered;
    }

    // KAN-325: gate on the INTENT to require a video, not on the resolved video
    // asset. `effectiveVideoRequirement` is computed upstream as
    // (enforceVideoCompletion && isVideoRequired), and isVideoRequired is only
    // true when the module actually has a video configured. Keying the gate off
    // this intent — rather than a resolved videoId/videoUrl — means a real
    // learner cannot slip past the gate when the video asset fails to resolve
    // (empty/mismatched videoId/videoUrl). We fail SAFE: keep the gate.
    const gateIntended =
      !!this.currentPlayerConfig.enforceVideoCompletion &&
      this.effectiveVideoRequirement &&
      this.requiredWatchPercentage > 0;

    if (!gateIntended) {
      // Genuinely un-gated: no video required, watch requirement set to "None"
      // (0%), or enforcement disabled by config. These are legitimate bypasses.
      return allQuestionsAnswered;
    }

    const currentUser = this.profileService.getCurrentUser();
    const userRole = currentUser?.currentOrgRole || currentUser?.role;
    const isInstructorOrAdmin = currentUser && (
      userRole === 'instructor' ||
      userRole === 'admin' ||
      currentUser.isSystemAdmin
    );

    if (isInstructorOrAdmin) {
      return allQuestionsAnswered;
    }

    const percentageMeetsRequirement = this.videoWatchPercentage >= this.requiredWatchPercentage;
    const videoMeetsRequirement = this.videoCompletedFromBackend || percentageMeetsRequirement;

    if (this.videoCompleted && !videoMeetsRequirement) {
      console.error('[QuizPanel] videoCompleted flag is true but actual percentage check failed');
    }

    return allQuestionsAnswered && videoMeetsRequirement;
  }

  /**
   * KAN-306: the actionable half of the unlock notice, beside the shared chip.
   * The chip states where the learner IS; this states what is left and the
   * threshold it is measured against — read from requiredWatchPercentage, which
   * is per-course, so it is never hardcoded to 80.
   *
   * Rounds the remainder UP: with 79.2% watched against an 80% gate, "1% more"
   * is honest where a rounded-down "0% more" would claim the quiz should
   * already be unlocked while it is still blocked. The gate itself is computed
   * in canSubmitQuiz() and is not affected by this display rounding.
   */
  get unlockHelperText(): string {
    const remaining = Math.max(0, Math.ceil(this.requiredWatchPercentage - this.videoWatchPercentage));
    return remaining > 0
      ? `${remaining}% more to unlock (${this.requiredWatchPercentage}% required)`
      : `${this.requiredWatchPercentage}% required to unlock`;
  }

  canOverrideVideoRequirement(): boolean {
    const currentUser = this.profileService.getCurrentUser();
    const userRole = currentUser?.currentOrgRole || currentUser?.role;
    return !!(currentUser && (
      userRole === 'instructor' ||
      userRole === 'admin' ||
      currentUser.isSystemAdmin
    ));
  }

  async submitQuiz(): Promise<void> {
    if (!this.canSubmitQuiz()) return;

    // Immediately show loading state to prevent double-clicks and give user feedback
    this.quizSubmitting = true;

    // Force-save the current video position and wait for all saves to complete.
    // This prevents a race condition where the backend checks video_watch_percentage
    // from the DB, which may lag behind the frontend's local state by up to 10 seconds
    // due to throttled saves.
    try {
      await this.videoProgressService.flushProgress();
    } catch (error) {
      console.warn('[QuizPanel] Failed to flush video progress before quiz submit:', error);
      // Continue with quiz submission even if flush fails
    }

    // Capture video progress snapshot to send with submission.
    // The backend uses this to write progress before checking the video completion requirement,
    // eliminating the race condition where the DB hasn't updated yet.
    const progressSnapshot = this.videoProgressService.getProgressSnapshot();
    const videoProgress = progressSnapshot.duration > 0
      ? { furthestPosition: progressSnapshot.furthestPosition, duration: progressSnapshot.duration }
      : undefined;

    if (this.useServerScoring) {
      // SERVER-AUTHORITATIVE: Don't grade locally, wait for server
      this.quizSubmitEmitted = true;
      this.quizSubmit.emit({ answers: this.userAnswers, score: null, passed: null, videoProgress, attemptId: this.attemptId || undefined });
    } else {
      // INSTRUCTOR PREVIEW: Grade locally, show immediately
      const result = gradeQuizForPreview(this.quizQuestions, this.userAnswers);

      this.quizFeedback = result.feedback;
      this.autoGradedScore = result.autoEarnedPoints;
      this.autoGradedMaxScore = result.autoGradablePoints;
      // Followup KAN-225: capture full total (incl. manual-grade points) for
      // the header. autoGradedMaxScore excludes manual on this path.
      this.submittedTotalPoints = result.totalPoints;
      this.isPartiallyGraded = result.manualGradingPoints > 0;
      // KAN-239: display uses the integer-rounded score, but pass/fail MUST use
      // the raw (1dp) percentage so a sub-threshold score (e.g. 79.5% < 80%) is
      // never rounded up into a PASS.
      this.quizScore = result.percentageScore;
      this.quizPassed = this.isPartiallyGraded
        ? false
        : isQuizPassing(result.rawPercentageScore, this.passingScore);
      // KAN-309: the server-scored path receives the gated custom incorrect
      // message in the submission response; instructor preview grades locally and
      // has none, so apply the SAME KAN-128 gate here (score_only suppresses the
      // per-result guidance). The template's `!quizPassed` check controls fail-only
      // display, mirroring the learner path exactly.
      this.wrongAnswerFeedback =
        (this.customIncorrectMessage && !isScoreOnly(this.getEffectiveFeedbackFlags()))
          ? this.customIncorrectMessage
          : null;
      // KAN-327: instructor preview grades client-side and has no submission
      // response, so apply the same PASS-only gate here. The template's
      // `quizPassed` check controls pass-only display, mirroring the learner path.
      this.passFeedback =
        (this.customPassMessage && !isScoreOnly(this.getEffectiveFeedbackFlags()))
          ? this.customPassMessage
          : null;
      this.quizSubmitting = false;
      this.quizSubmitted = true;
      this.showFeedback = true;

      this.syncService.updateQuizState({
        isSubmitted: true,
        score: this.quizScore
      });

      this.quizSubmit.emit({
        answers: this.userAnswers,
        score: this.quizScore,
        passed: this.quizPassed
      });

      if (this.moduleId) {
        this.progressUpdate.emit({
          moduleId: this.moduleId,
          videoProgress: this.videoWatchPercentage,
          quizScore: this.quizScore,
          completed: this.quizPassed
        });
      }
    }
  }

  retryQuiz(): void {
    // KAN-314 VS1: never start a fresh attempt once the cap is reached. The
    // button is hidden in this state, but guard the method too (the server
    // rejects an over-limit submission with 409 regardless).
    if (this.noAttemptsRemaining) {
      return;
    }

    // KAN-478: carry the pass forward before `quizPassed` is cleared below. A
    // retake never revokes a pass (migration 135 derives completion from the
    // earliest VALID passing submission, which this attempt cannot remove), and
    // without this a same-session retake would read as "never passed" until the
    // next server round-trip — long enough to strand the learner on the last
    // item once attempts run out.
    this.hasPassedAttempt = this.quizEverPassed;

    // Preserve manual-graded answers on guaranteed fail retry
    const preservedAnswers = new Map<number, any>();
    if (this.guaranteedFail && this.quizQuestions) {
      this.quizQuestions.forEach((q, i) => {
        if ((q.type === 'paragraph' || q.type === 'short-answer') &&
            this.quizFeedback[i]?.requiresManualGrading && this.userAnswers[i]) {
          preservedAnswers.set(i, this.userAnswers[i]);
        }
      });
    }

    this.userAnswers = new Array(this.quizQuestions.length).fill(null);
    this.quizSubmitted = false;
    this.quizSubmitting = false;
    this.showFeedback = false;
    this.quizScore = 0;
    this.quizPercentage = null;
    this.quizPassed = false;
    this.quizFeedback = [];
    this.wrongAnswerFeedback = null;
    this.passFeedback = null;

    this.isViewingPreviousSubmission = false;
    this.viewingManualGrant = false;
    this.viewingPriorCredit = false;
    this.autoGradedScore = 0;
    this.autoGradedMaxScore = 0;
    this.submittedTotalPoints = null;
    this.submissionDate = null;

    // Reset smart retry state
    this.gradingStatus = null;
    this.guaranteedFail = false;
    this.manualPendingPoints = 0;
    this.bestPossiblePercentage = 0;
    this.isPartiallyGraded = false;

    // Re-fill preserved manual answers
    preservedAnswers.forEach((answer, index) => {
      this.userAnswers[index] = answer;
    });

    // Re-shuffle display order for a fresh attempt
    this.computeDisplayOrder();

    // KAN-348: a learner retake starts a FRESH attempt (new draw) — the prior
    // attempt was consumed by its submission. KAN-345: clear the in-flight guard
    // so the retake's attempt-start is not deduped against the prior attempt.
    this.attemptId = null;
    this.attemptStartInFlight = false;
    this.attemptUpdatedNotice = false;
    this.startOrResumeAttempt();

    this.syncService.updateQuizState({
      isSubmitted: false,
      score: undefined,
      currentQuestionIndex: 0
    });
  }

  // ─── Debug auto-fill ────────────────────────────────────────────

  autoFillQuiz(): void {
    if (!this.quizId) return;
    this.quizService.getDebugAnswers(this.quizId).pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe({
      next: (data) => {
        data.answers.forEach((answer) => {
          if (answer.questionIndex >= this.quizQuestions.length) return;

          const type = answer.questionType;
          if (type === 'multiple-choice' || type === 'true-false') {
            // For MC, find the correct option index; for T/F, use the correctAnswer directly
            if (type === 'true-false') {
              this.userAnswers[answer.questionIndex] = answer.correctAnswer;
            } else {
              const correctIdx = answer.options.findIndex((o: any) => o.isCorrect);
              if (correctIdx >= 0) {
                this.userAnswers[answer.questionIndex] = correctIdx;
              }
            }
          } else if (type === 'short-answer') {
            this.userAnswers[answer.questionIndex] = answer.correctAnswer || '';
          } else if (type === 'select-all') {
            const correctIndices = answer.options
              .map((o: any, idx: number) => o.isCorrect ? idx : -1)
              .filter((idx: number) => idx >= 0);
            this.userAnswers[answer.questionIndex] = correctIndices;
          } else if (type === 'paragraph') {
            this.userAnswers[answer.questionIndex] = answer.correctAnswer || 'Auto-filled paragraph answer';
          } else if (type === 'multiple-choice-grid') {
            // KAN-274: MC-grid is multi-select. Build the boolean[] per row that
            // exactly matches the accepted-column set (single int, array of
            // indices, or [] for a no-answer row all collapse to a boolean[]).
            if (Array.isArray(answer.correctAnswer)) {
              const numCols = this.quizQuestions[answer.questionIndex]?.gridColumns?.length || 0;
              this.userAnswers[answer.questionIndex] = answer.correctAnswer.map((cell: any) => {
                const row = new Array(numCols).fill(false);
                for (const idx of mcGridAcceptedColumnsFn(cell)) {
                  if (idx >= 0 && idx < numCols) row[idx] = true;
                }
                return row;
              });
            }
          } else if (type === 'checkbox-grid') {
            // correctAnswer is expected as a 2D boolean array [row][col]
            if (Array.isArray(answer.correctAnswer)) {
              this.userAnswers[answer.questionIndex] = answer.correctAnswer.map(
                (row: any) => Array.isArray(row) ? [...row] : row
              );
            }
          } else if (type === 'matching') {
            // KAN-333: correctAnswer is the number[] index array (answer[i] =
            // matchRight index for matchLeft[i]).
            if (Array.isArray(answer.correctAnswer)) {
              this.userAnswers[answer.questionIndex] = [...answer.correctAnswer];
            }
          }
        });
        this.cdr.markForCheck();
      },
      error: (err) => {
        console.error('[QuizPanel] Auto-fill failed:', err);
      }
    });
  }

  // ─── Learner attempt (KAN-348) — start/resume + frozen snapshot ──

  /**
   * KAN-414: instructor preview/test resolve bank slots through the stateless
   * preview draw (no attempt row, no draw history). KAN-386 carries the raw
   * slots as the nav payload so the panel's first-mount start fires; for a
   * learner startOrResumeAttempt swaps them for the attempt's frozen snapshot,
   * but preview modes have no attempt — without this they rendered the slots
   * raw (a points badge above an empty body). Fires only when a slot actually
   * needs resolving, so plain fixed-quiz previews are untouched.
   */
  private startPreviewDraw(): void {
    if (this.attemptStartInFlight) return;

    this.attemptLoadError = null;
    this.attemptStartFailed = false;
    this.drawFailedBankId = null;
    this.attemptStartInFlight = true;

    this.quizService.previewDraw(this.quizId!).pipe(
      finalize(() => { this.attemptStartInFlight = false; }),
      takeUntilDestroyed(this.destroyRef),
      catchError(error => {
        // A preview that cannot draw must say so — the alternative is the
        // unresolved slots rendering as empty cards (the KAN-414 defect).
        const code = error?.error?.error?.code;
        const details = error?.error?.error?.details;
        this.attemptStartFailed = true;
        this.drawFailedBankId = details?.bankId ?? null;
        this.attemptLoadError = code === 'ATTEMPT_DRAW_FAILED' && this.drawFailedBankId
          ? `This quiz could not draw a question from its bank (${this.drawFailedBankId}). Please retry.`
          : 'Unable to load a preview of this quiz. Please try again.';
        return of(null);
      })
    ).subscribe((questions: QuizAttemptQuestion[] | null) => {
      if (Array.isArray(questions) && questions.length > 0) {
        this.quizQuestions = questions as unknown as QuizQuestion[];
        this.userAnswers = new Array(this.quizQuestions.length).fill(null);
        this.computeDisplayOrder();
        this.checkForManualGradingQuestions();
        this.syncService.updateQuizState({
          totalQuestions: this.quizQuestions.length,
          isLocked: this.isVideoRequired && !this.videoCompleted
        });
      }
      this.cdr.markForCheck();
    });
  }

  private startOrResumeAttempt(): void {
    if (!this.quizId) return;
    if (this.mode !== 'learner') {
      // KAN-414: preview modes have no attempt, but bank slots still need
      // resolving before anything is renderable.
      if (this.awaitingDraw) this.startPreviewDraw();
      return;
    }

    // KAN-345 (resume-clobber): one attempt-start POST per entry. Skip if a
    // start is already in flight — the duplicate ngOnChanges trigger fires
    // while the first POST is outstanding, and its late resumed response is
    // what re-applied savedAnswers and clobbered a click. A genuine
    // quizId/placement change and a retake (retryQuiz) clear the flag first, so
    // they still start a fresh attempt. (The non-destructive savedAnswers merge
    // is the second line of defense if a duplicate ever slips through.)
    if (this.attemptStartInFlight) return;

    this.attemptLoadError = null;
    this.attemptStartFailed = false;
    this.attemptsExhaustedAtStart = false;
    this.drawFailedBankId = null;
    this.moduleLocked = false;
    this.moduleLockedReason = null;
    this.attemptStartInFlight = true;
    const ctx = { ...(this.getPlacementScope() ?? {}), courseId: this.courseId };

    this.quizService.startAttempt(this.quizId, ctx).pipe(
      finalize(() => { this.attemptStartInFlight = false; }),
      catchError(error => {
        // KAN-349: attempt cap reached — a Retry can't help; render the
        // attempts-exhausted state (details carry attemptsUsed/attemptLimit).
        const code = error?.error?.error?.code;
        const details = error?.error?.error?.details;
        if (code === 'ATTEMPT_LIMIT_REACHED') {
          this.attemptsExhaustedAtStart = true;
          this.attemptLimit = details?.attemptLimit ?? this.attemptLimit ?? null;
          this.attemptsUsed = details?.attemptsUsed ?? null;
          this.attemptsRemaining = 0;
          return of(null);
        }

        // KAN-332 QA Fix 4: a locked module/unit must NEVER fall through to
        // the fail-open path below - that path exists for genuine start
        // failures on quizzes we can still safely grade by identity mapping.
        // A lock is an active "you may not take this yet" gate, not a
        // failure to recover from; falling open here would render (and let
        // the learner submit) the authored questions despite the server
        // refusing to start an attempt - an info leak plus a functional
        // bypass of the sequential-access gate.
        if (code === 'MODULE_LOCKED' || code === 'UNIT_LOCKED') {
          this.quizQuestions = [];
          this.moduleLocked = true;
          this.moduleLockedReason = error?.error?.error?.message || 'This quiz is locked until you complete the required content first.';
          return of(null);
        }

        // KAN-348 review: plain fixed quizzes stay fail-OPEN (authored
        // questions render + submit; grading is identity-mapped without an
        // attempt). Quizzes that REQUIRE a draw (bank slots / shuffle) fail
        // CLOSED via attemptSubmitBlocked — banner + Retry, submit disabled.
        console.warn('[QuizPanel] Failed to start attempt, using authored questions:', error);
        this.attemptLoadError = 'Unable to start this quiz. Please try again.';
        this.attemptStartFailed = true;
        return of(null);
      })
    ).subscribe((attempt: QuizAttempt | null) => {
      if (attempt && attempt.attemptId) {
        this.attemptId = attempt.attemptId;
        this.attemptStartFailed = false;
        this.attemptExpired = false;
        if (attempt.refreshed) {
          this.attemptUpdatedNotice = true;
        }
        // KAN-349: drive the countdown from the server-authoritative deadline.
        this.startExpiryCountdown(attempt.expiresAt ?? null);
        if (attempt.passingScore != null) {
          this.passingScore = attempt.passingScore;
        }

        if (Array.isArray(attempt.questions) && attempt.questions.length > 0) {
          const priorAnswers = this.userAnswers;
          const priorQuestions = this.quizQuestions;
          this.quizQuestions = attempt.questions as unknown as QuizQuestion[];
          this.questionsFromSnapshot = true;
          if (attempt.resumed && Array.isArray(attempt.savedAnswers) && attempt.savedAnswers.length > 0) {
            // KAN-348 Δ3: autosaved answers are stored against THIS attempt's
            // frozen snapshot (display space), so they map onto the snapshot
            // by construction. Consistency guard (review F2): a shape mismatch
            // means they can't be trusted — discard rather than mis-align.
            //
            // KAN-345 (resume-clobber): merge NON-destructively — saved answers
            // fill only the slots the learner has NOT already set locally. A
            // genuine reload restores fully (nothing is set locally), but a click
            // made in the ~1s window before a late/duplicate attempt-start
            // response is preserved instead of being clobbered.
            //
            // KAN-678: local answers are positional against the PRIOR question
            // list, which a refreshed attempt (instructor edit) can reorder or
            // extend - carry them onto their own questions before merging.
            const localAnswers = carryAnswersForIdenticalQuestions(priorAnswers, priorQuestions, this.quizQuestions);
            this.userAnswers = attempt.savedAnswers.length === this.quizQuestions.length
              ? mergeSavedAnswersPreservingLocal(localAnswers, attempt.savedAnswers, this.quizQuestions.length)
              : new Array(this.quizQuestions.length).fill(null);
          } else if (this.hasInProgressAnswers()) {
            // KAN-271 + KAN-348 review: the snapshot may be shuffled or
            // bank-drawn — carrying by index would silently corrupt answers.
            // Carry ONLY content-identical questions; drop the rest.
            this.userAnswers = carryAnswersForIdenticalQuestions(
              priorAnswers, priorQuestions, this.quizQuestions
            );
          } else {
            this.userAnswers = new Array(this.quizQuestions.length).fill(null);
          }
          this.computeDisplayOrder();
          this.checkForManualGradingQuestions();
          this.syncService.updateQuizState({
            totalQuestions: this.quizQuestions.length,
            isLocked: this.isVideoRequired && !this.videoCompleted
          });
        }
        console.log('[QuizPanel] Attempt started', { attemptId: this.attemptId, resumed: attempt.resumed });
      }
      this.cdr.markForCheck();
    });
  }

  /** KAN-348 review: user-facing Retry for a failed attempt start. */
  retryAttemptStart(): void {
    this.startOrResumeAttempt();
  }

  // ─── KAN-348 Δ3: answer autosave onto the open attempt ─────────────

  private get canAutosave(): boolean {
    return this.mode === 'learner'
      && !!this.quizId
      && !!this.attemptId
      && !this.quizSubmitted
      && !this.quizSubmitting;
  }

  /** Queue a debounced autosave after an answer mutation. */
  private queueAutosave(): void {
    if (this.canAutosave) {
      this.autosaveTrigger$.next();
    }
  }

  /** Immediate best-effort save (debounce flush / tab hidden). Failures are
   *  swallowed — the next answer change re-queues, and submit is the source
   *  of truth anyway. */
  private flushAutosave(): void {
    if (!this.canAutosave) return;
    this.quizService.saveAttemptAnswers(this.quizId!, this.attemptId!, this.userAnswers)
      .subscribe({ error: () => { /* silent best-effort */ } });
  }

  /** Template hook for ngModel-bound inputs (short answer / paragraph). */
  onAnswerModelChanged(): void {
    this.queueAutosave();
  }

  // ─── KAN-349: attempt deadline countdown + lifecycle 409 recovery ──

  private startExpiryCountdown(expiresAt: string | null): void {
    this.clearExpiryCountdown();
    this.attemptExpiresAt = expiresAt;
    if (!expiresAt) {
      this.attemptRemainingSeconds.set(null);
      return;
    }

    const deadline = new Date(expiresAt).getTime();
    if (!Number.isFinite(deadline)) {
      this.attemptRemainingSeconds.set(null);
      return;
    }

    const tick = () => {
      if (this.quizSubmitted) {
        // Submission landed — the deadline no longer matters.
        this.clearExpiryCountdown();
        this.attemptRemainingSeconds.set(null);
        return;
      }
      const remaining = Math.max(0, Math.ceil((deadline - Date.now()) / 1000));
      if (remaining <= 0) {
        // Plain-field write is safe: the signal write below marks the view
        // dirty, and the template re-reads attemptExpired during that render.
        this.attemptExpired = true;
        this.clearExpiryCountdown();
      }
      this.attemptRemainingSeconds.set(remaining);
    };

    tick();
    if (!this.attemptExpired) {
      this.expiryTimer = setInterval(tick, 1000);
    }
  }

  private clearExpiryCountdown(): void {
    if (this.expiryTimer) {
      clearInterval(this.expiryTimer);
      this.expiryTimer = null;
    }
  }

  /** mm:ss for the countdown banner. */
  formatRemaining(totalSeconds: number): string {
    const m = Math.floor(totalSeconds / 60);
    const sec = totalSeconds % 60;
    return `${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
  }

  /** KAN-349: expired-attempt CTA — new draw via the normal retake path. */
  startFreshAttempt(): void {
    this.attemptExpired = false;
    this.attemptRemainingSeconds.set(null);
    this.retryQuiz();
  }

  /**
   * KAN-349: dedicated recovery for attempt-lifecycle submit 409s. The learner
   * modal suppresses the generic alert for these codes and threads the parsed
   * info here via the quizSubmissionErrorInfo input.
   */
  private handleSubmitErrorInfo(info: { code?: string; details?: any }): void {
    switch (info.code) {
      case 'ATTEMPT_EXPIRED':
        this.quizSubmitting = false;
        this.quizSubmitEmitted = false;
        this.attemptId = null;
        this.attemptExpired = true;
        this.clearExpiryCountdown();
        this.attemptRemainingSeconds.set(0);
        break;
      case 'ATTEMPT_ALREADY_SUBMITTED':
        // The attempt was already consumed (double-submit / second tab) —
        // load the recorded results instead of leaving a stuck spinner.
        this.quizSubmitting = false;
        this.quizSubmitEmitted = false;
        this.attemptId = null;
        this.fetchAndPrefillSubmission();
        break;
      case 'ATTEMPT_SUPERSEDED':
        // KAN-678: the instructor changed the quiz and another load (another
        // tab, or a reload) re-froze the attempt. Load the current attempt -
        // answers follow their own questions - and let the learner review it
        // and submit again, never grading these answers against the old list.
        this.quizSubmitting = false;
        this.quizSubmitEmitted = false;
        this.attemptId = null;
        this.attemptUpdatedNotice = true;
        this.startOrResumeAttempt();
        break;
      case 'ATTEMPT_DRAW_FAILED':
        this.quizSubmitting = false;
        this.quizSubmitEmitted = false;
        this.drawFailedBankId = info.details?.bankId ?? null;
        this.attemptLoadError = this.drawFailedBankId
          ? `This quiz could not draw a question from its bank (${this.drawFailedBankId}). Please retry.`
          : 'This quiz could not draw its questions. Please retry.';
        // Reuse the fail-closed + Retry affordance from the start-failure path.
        this.attemptStartFailed = true;
        break;
    }
    this.cdr.markForCheck();
  }

  // ─── Submission pre-fill methods ──────────────────────────────────

  /**
   * KAN-270: resolve THIS placement's scope so the prior-submission fetch is
   * isolated from sibling placements of a reused quiz. A module placement keys
   * by moduleId; an end-of-unit placement keys by unitId. The `moduleId` @Input
   * carries the navigation node id for BOTH item types (it is `${unitId}_quiz`
   * for a unit quiz), so derive the bare unit id the same way the submit path
   * does (learner-quiz.service: `rawId.replace('_quiz', '')`).
   */
  private getPlacementScope(): { moduleId?: string; unitId?: string } | undefined {
    if (!this.moduleId) {
      return undefined;
    }
    if (this.itemType === 'unit_quiz') {
      return { unitId: this.moduleId.replace('_quiz', '') };
    }
    return { moduleId: this.moduleId };
  }

  private fetchAndPrefillSubmission(): void {
    if (this.mode !== 'learner') {
      this.initializeEmptyQuiz();
      return;
    }
    if (!this.quizId) {
      this.initializeEmptyQuiz();
      return;
    }

    // Cancel any in-flight fetch from a previous quizId (KAN-120: prevents race condition)
    this.fetchCancel$.next();

    this.quizService.getLatestSubmission(this.quizId, undefined, this.courseId, this.getPlacementScope()).pipe(
      takeUntil(this.fetchCancel$),
      takeUntilDestroyed(this.destroyRef),
      retry({
        count: 3,
        delay: (error, retryCount) => {
          console.log(`[QuizPanel] Retry ${retryCount}/3 fetching submission`);
          return of(null).pipe(delay(100 * Math.pow(2, retryCount - 1)));
        }
      }),
      catchError(error => {
        console.error('[QuizPanel] Failed to fetch submission after retries:', error);
        this.showErrorBanner('Could not load previous answers');
        this.initializeEmptyQuiz();
        return of(null);
      })
    ).subscribe((submission: QuizSubmission | null) => {
      if (submission && submission.answers) {
        this.prefillQuizFromSubmission(submission);
      } else {
        this.initializeEmptyQuiz();
      }
      this.cdr.markForCheck();
    });
  }

  private prefillQuizFromSubmission(submission: QuizSubmission): void {
    if (!Array.isArray(submission.answers) && typeof submission.answers !== 'object') {
      this.showErrorBanner('Unable to display previous answers');
      this.initializeEmptyQuiz();
      return;
    }

    // KAN-348 Δ5: review renders the ATTEMPTED (snapshot) questions when the
    // submission carries them, so the learner sees exactly what they answered
    // (bank draws / shuffled order) — not the current authored questions.
    if (Array.isArray(submission.questions) && submission.questions.length > 0) {
      this.quizQuestions = submission.questions as unknown as QuizQuestion[];
      this.questionsFromSnapshot = true;
      this.computeDisplayOrder();
      this.checkForManualGradingQuestions();
    }

    const questionCountMismatch = submission.answers.length !== this.quizQuestions.length;

    if (questionCountMismatch) {
      this.showQuestionMismatchBanner = true;
    }

    this.userAnswers = this.matchAnswersToQuestions(submission.answers);
    this.quizScore = submission.score;
    // KAN-127 follow-up: capture the canonical 1-dp percentage (Phase 6 backend)
    // from the prefilled submission so the banner clamp shows the true value when a
    // learner revisits a near-boundary submission (e.g. 79.5% Failed, not "(80%)").
    this.quizPercentage = submission.percentage ?? null;
    this.submissionDate = submission.submittedAt;
    this.isViewingPreviousSubmission = true;
    // KAN-334: a granted submission has no real attempt — the banner labels it.
    this.viewingManualGrant = submission.isManualGrant === true;
    this.viewingPriorCredit = submission.isPriorCredit === true;

    // KAN-314 VS1: capture attempts so the result view can withhold "Try Again"
    // once the cap is reached (survives refresh/resume).
    this.attemptLimit = submission.attemptLimit ?? null;
    this.attemptsRemaining = submission.attemptsRemaining ?? null;
    // KAN-478: the record-level pass, which a restored FAILED retake cannot
    // carry on its own `passed`. Absent on a pre-KAN-478 backend — `quizEverPassed`
    // then falls back to this submission's own verdict (pre-fix behavior).
    this.hasPassedAttempt = submission.hasPassedAttempt === true;

    // Set feedback visibility from submission response (KAN-69)
    // KAN-343/KAN-219: clamp so a relaxed/omitted server value can't loosen the
    // configured level. Prefers the flags; falls back to the legacy scalar.
    this.applyServerFeedbackFlags(submission.feedbackFlags ?? submission.feedbackVisibility);

    if (submission.questionResults) {
      if (submission.questionResults.length !== this.quizQuestions.length) {
        console.warn('[QuizPanel] questionResults count mismatch');
      }
      this.questionResults = submission.questionResults;

      this.updateQuizFeedbackFromBackend(submission.questionResults);
    } else {
      this.questionResults = null;
    }

    this.autoGradedScore = submission.earnedPoints ?? submission.score ?? 0;
    // Use the raw live sum as fallback (NOT this.getTotalQuizPoints(), which
    // prefers submittedTotalPoints — still null on this line until it's set
    // below, but make the intent explicit to avoid subtle ordering bugs).
    // KAN-430 card #61: liveTotalQuizPoints() routes through the bank-aware
    // this.getQuestionPoints (unlike the shared getTotalQuizPoints, which has
    // no slotType==='bank' branch and fabricated 1 pt per unresolved slot on
    // a legacy submission with no persisted totalPoints).
    this.autoGradedMaxScore = submission.totalPoints ?? this.liveTotalQuizPoints();
    // Followup KAN-225: track stored total so the "N pts total" header stays
    // consistent with the score badge denominator when viewing a historical
    // submission whose quiz has since been edited.
    this.submittedTotalPoints = submission.totalPoints ?? null;

    // KAN-239: round to 1dp HALF_UP (backend semantics) before comparing, so a
    // restored sub-threshold submission never passes via display rounding.
    const percentageScore = this.autoGradedMaxScore > 0
      ? (this.autoGradedScore / this.autoGradedMaxScore) * 100
      : 0;
    this.quizPassed = isQuizPassing(percentageScore, this.passingScore);

    // Detect pending manual grading on revisit/refresh
    if (isAwaitingGrading(submission.gradingStatus)) {
      this.isPartiallyGraded = true;
      this.gradingStatus = submission.gradingStatus ?? null;
      this.guaranteedFail = submission.guaranteedFail ?? false;
      this.manualPendingPoints = submission.manualPendingPoints ?? 0;
      this.bestPossiblePercentage = submission.bestPossiblePercentage ?? 0;
      this.quizPassed = false;
    }

    // If overridden by instructor, treat as fully graded
    if (submission.overriddenScore != null) {
      this.isPartiallyGraded = false;
      this.gradingStatus = null;
    }

    // KAN-310: restore the custom incorrect ("Note from instructor") message so
    // the failure banner survives "Resume Course". The backend already gates the
    // field (failure + non-score_only), identical to the live-submit response;
    // the template's `!quizPassed` check keeps it failure-only here too.
    this.wrongAnswerFeedback = submission.wrongAnswerFeedback ?? null;
    // KAN-327: restore the gated custom pass message so the success banner
    // survives "Resume Course" (backend gates it to a PASS + non-score_only).
    this.passFeedback = submission.passFeedback ?? null;

    this.showFeedback = true;
    this.quizSubmitted = true;

    // Notify parent so status bar badge updates
    this.submissionRestored.emit({
      quizSubmitted: this.quizSubmitted,
      quizPassed: this.quizPassed,
      autoGradedScore: this.autoGradedScore,
      autoGradedMaxScore: this.autoGradedMaxScore,
      isPartiallyGraded: this.isPartiallyGraded,
      guaranteedFail: this.guaranteedFail,
      gradingStatus: this.gradingStatus
    });
  }

  private matchAnswersToQuestions(submissionAnswers: any[]): any[] {
    return matchAnswersToQuestionsFn(submissionAnswers, this.quizQuestions.length);
  }

  private initializeEmptyQuiz(): void {
    // KAN-271: the no-prior-submission branch of fetchAndPrefillSubmission()
    // resolves asynchronously AFTER navigation, by which point the learner may
    // have already clicked an answer on the freshly-rendered quiz. Don't wipe an
    // in-progress selection — only blank the answers when nothing was entered
    // yet (true empty init / placement change before any interaction).
    if (!this.hasInProgressAnswers()) {
      this.userAnswers = new Array(this.quizQuestions.length).fill(null);
    }
    this.isViewingPreviousSubmission = false;
    this.questionResults = null;
    // KAN-348: no prior submission → a learner takes the quiz through a fresh
    // (or resumed) attempt. Instructor preview stays on authored questions.
    this.startOrResumeAttempt();
  }

  /**
   * KAN-271: true when the learner has an unsubmitted, partially-answered quiz on
   * screen (any answer slot populated). Used to stop late async resets (submission
   * fetch / session start that fire ~200ms after navigation) from clobbering the
   * user's first click. A submitted quiz never counts as "in progress".
   */
  private hasInProgressAnswers(): boolean {
    if (this.quizSubmitted) return false;
    return this.userAnswers.some(answer =>
      Array.isArray(answer) ? answer.length > 0 : (answer !== null && answer !== undefined)
    );
  }

  private showErrorBanner(message: string): void {
    console.error('[QuizPanel] Error:', message);
  }

  private updateQuizFeedbackFromBackend(questionResults: any[]): void {
    const builtFeedback = buildFeedbackFromBackendResults(questionResults, this.quizQuestions);
    if (this.quizFeedback.length === 0) {
      this.quizFeedback = builtFeedback;
    } else {
      builtFeedback.forEach((item, index) => {
        if (index < this.quizFeedback.length) {
          this.quizFeedback[index] = item;
        }
      });
    }
  }

  // ─── Delegating helpers (pure logic in quiz-grading.utils.ts) ────

  checkForManualGradingQuestions(): void {
    const result = checkForManualGradingQuestions(this.quizQuestions);
    this.hasManualGradingQuestions = result.hasManual;
    this.manualGradingPending = result.pendingCount;
  }

  getWordCount(text: string): number {
    return getWordCount(text);
  }

  getQuestionPoints(question: QuizQuestion): number {
    // KAN-430: an unresolved bank-draw slot ({ slotType:'bank', bankId,
    // pointsOverride }) carries no intrinsic points and is NOT a question —
    // the shared getQuestionPoints below has no bank-slot branch, so it fell
    // through to the generic `points ?? 1` tail and fabricated 1 point per
    // slot. Semantics mirror the modal header's KAN-347 decision
    // (quiz-view-modal.component.ts getQuestionPoints): count a set
    // pointsOverride, otherwise contribute 0 — never invent a value. A
    // learner's attempt snapshot always resolves slots before this renders
    // (KAN-386/414), so this branch is a no-op on the learner path.
    if ((question as any)?.slotType === 'bank') {
      const override = (question as any).pointsOverride;
      return typeof override === 'number' && Number.isFinite(override) ? override : 0;
    }
    // KAN-348 QA: attempt snapshots freeze the EFFECTIVE points on
    // question.points for EVERY type (slot overrides + grid budget chain
    // resolved server-side; the backend guarantees it equals what grading
    // awards). Authored renders keep the KAN-241 type-specific computation.
    if (this.questionsFromSnapshot && typeof (question as any)?.points === 'number') {
      return (question as any).points;
    }
    return getQuestionPoints(question);
  }

  getPerRowPoints(question: QuizQuestion, rowIndex?: number): number {
    // KAN-348 QA: the per-row "+X" chips share the snapshot-aware budget so
    // they match the badge/header and what grading actually awards.
    return getPerRowPoints(question, rowIndex, this.getQuestionPoints(question));
  }

  /**
   * Returns the "N pts total" denominator shown in the quiz header.
   *
   * Reconciles the header total with the score badge denominator so they
   * never disagree (followup KAN-225). Two cases:
   *
   * 1. Live quiz-taking (no submission displayed) — sum the current question
   *    array. This is the only authoritative source because no submission
   *    has been recorded yet.
   *
   * 2. Viewing a submitted attempt — return `submittedTotalPoints`, which is
   *    the stored `quiz_submissions.total_points` from when the learner
   *    actually submitted. The earned points (numerator in the score badge)
   *    were measured against THAT total, not the current (possibly edited)
   *    question array. Showing `9 pts total` next to `5.5/10 pts` is
   *    nonsensical — the historical total keeps the displayed fraction
   *    arithmetically honest.
   *
   * `submittedTotalPoints` (not `autoGradedMaxScore`) is the right source
   * because the latter excludes manual-grading points on some paths
   * (instructor live preview), which would shrink the header total
   * inappropriately for partially-graded quizzes.
   *
   * If `submittedTotalPoints` is unset for any reason, fall back to the live
   * sum rather than showing `0 pts total`.
   */
  getTotalQuizPoints(): number {
    if (this.quizSubmitted && this.submittedTotalPoints != null && this.submittedTotalPoints > 0) {
      return this.submittedTotalPoints;
    }
    return this.liveTotalQuizPoints();
  }

  /**
   * KAN-430: the header badge text. An instructor preview (content-hub
   * quiz-view modal body) and a learner attempt both call getTotalQuizPoints,
   * but they can legitimately sum DIFFERENT question lists — the preview
   * resolves bank-draw slots via previewDraw, the modal's own header sums the
   * still-authored definition. Labelling both "N pts total" asserted a false
   * equivalence. Only the instructor preview wording changes here; a learner
   * attempt must render byte-identical to before this change.
   *
   * WHY 'instructor_test' IS DELIBERATELY EXCLUDED (do not "fix" this).
   * A code review filed this as a miss, on correct mechanics: startOrResumeAttempt
   * above takes the `mode !== 'learner'` branch, so instructor_test resolves
   * bank-draw slots through the SAME startPreviewDraw path as instructor_preview.
   * That much is true. It is still excluded. The exclusion rests on ONE argument;
   * a second observation corroborates it but does NOT carry it. The ordering is
   * deliberate - read it before challenging either.
   *
   * PRIMARY, AND LOAD-BEARING - design intent. Test Mode exists to reproduce the
   *    STUDENT experience verbatim: split-screen-player.utils.ts defines
   *    instructor_test as "Test Mode" / "Student experience + debug info", against
   *    instructor_preview = "Preview Mode" / "No restrictions, testing only".
   *    "N pts total" IS the student string, so relabelling it would break the
   *    fidelity that is the mode's entire reason to exist. This argument does not
   *    decay and cannot be falsified by finding another file - it is about what the
   *    mode is FOR. If you want to overturn this exclusion, you must argue against
   *    THIS, not against the corroboration below.
   *
   * CORROBORATING ONLY - an empirical check with a stated gap. The defect being
   *    fixed is a FALSE EQUIVALENCE between two differently-derived totals rendered
   *    TOGETHER, and that co-render does not appear to happen on the Test Mode
   *    surface: course-player-header.component.html renders exactly one numeric
   *    readout ("{{ currentIndex + 1 }} / {{ totalItems }}", an item counter) and
   *    getModeDisplayInfo returns only a badge name/colour, while the host
   *    course-player-learner-modal.component.html renders only totalUnitsCount /
   *    totalModulesCount / totalItems - all navigation counts, no points.
   *    TREAT THIS AS SUPPORT, NOT PROOF, for two independent reasons:
   *    (a) it is a SEARCH, and the host chrome and header component were verified
   *        while every child component Test Mode can mount was NOT exhaustively
   *        traced. Finding a competing authored total somewhere would weaken this
   *        observation - and would still leave the primary argument standing.
   *    (b) it does not generalise, so it cannot be the test. module-view-modal and
   *        unit-view-modal ALSO render no competing authored total, yet they
   *        correctly DO get the preview wording, because they are Preview Mode and
   *        the fidelity argument does not apply to them. A browser gate flagged
   *        exactly this. If "no competing total" were the rule, those two modals
   *        would have to revert - which is wrong. Design intent is the rule.
   *
   * Read the gating correctly: this getter DOES vary by mode - instructor_preview
   * gets the preview wording, while 'learner' and 'instructor_test' share the
   * student string. (Before this change the template at quiz-panel.component.html
   * line 16 interpolated `getTotalQuizPoints()` directly and was not gated at all.)
   * What is constant across modes is the NUMBER; the variable is whether a competing
   * authored total sits beside it.
   *
   * IF YOU CAME HERE TO RE-FILE THIS, push here: argue that Test Mode should NOT
   * reproduce the student string verbatim - i.e. that the design intent recorded
   * above is wrong or has changed. That is the real question and it is answerable.
   * Do NOT re-file on the mechanics (yes, instructor_test resolves slots via the
   * same path - that is stated above and is not in dispute), and do not re-file on
   * the corroborating search, which is bounded by construction and which the
   * exclusion does not depend on.
   */
  get totalPointsLabel(): string {
    return this.mode === 'instructor_preview'
      ? `${this.getTotalQuizPoints()} pts in this preview`
      : `${this.getTotalQuizPoints()} pts total`;
  }

  /**
   * Live (pre-submission) quiz total. KAN-348 QA: on snapshot renders this
   * sums the per-question EFFECTIVE points so the in-progress header matches
   * what grading will award (the old pure sum read stale authored mirrors and
   * under-counted overridden bank slots until submit "self-corrected" it).
   *
   * KAN-430: both branches now route through this.getQuestionPoints, which is
   * bank-slot aware. The non-snapshot branch previously called the SHARED
   * getTotalQuizPoints directly, bypassing that awareness entirely — that was
   * exactly the path the instructor quiz-view modal's <app-quiz-panel> body
   * takes (its questions are never snapshot-sourced), so an unresolved bank
   * slot fabricated 1 point there while the modal header (already bank-aware)
   * showed 0. Unifying the two branches makes body and header agree.
   */
  private liveTotalQuizPoints(): number {
    return (this.quizQuestions ?? []).reduce((sum, q) => sum + this.getQuestionPoints(q), 0);
  }

  /**
   * KAN-311: a quiz worth 0 total points is an ungraded acknowledgement that
   * mathematically cannot be failed. The raw percentage on such a quiz is 0%
   * (0/0), which `isQuizPassing` reads as a fail, so without this guard the
   * "Note from instructor" custom-incorrect banner wrongly renders. Gating on
   * the quiz's intrinsic point worth suppresses the banner only for the
   * ungraded case while leaving every graded (>0 pts) failed quiz untouched.
   */
  get isUngradedQuiz(): boolean {
    return this.liveTotalQuizPoints() === 0;
  }

  /** Build optionDisplayOrder for each question.
   *  Shuffles indices for MC/Select All questions with randomizeOptions=true in learner mode.
   *  Otherwise returns identity order [0, 1, 2, ...]. */
  computeDisplayOrder(): void {
    this.optionDisplayOrder = this.quizQuestions.map(question => {
      const optionCount = question.options?.length ?? 0;
      const identity = Array.from({ length: optionCount }, (_, idx) => idx);

      if (question.randomizeOptions === true &&
          this.mode === 'learner' &&
          (question.type === 'multiple-choice' || question.type === 'select-all')) {
        return this.shuffleArray(identity);
      }

      return identity;
    });
  }

  private shuffleArray(arr: number[]): number[] {
    const shuffled = [...arr];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  }

  private resetQuizState(): void {
    this.userAnswers = new Array(this.quizQuestions.length).fill(null);
    this.quizSubmitted = false;
    this.quizSubmitting = false;
    this.quizSubmitEmitted = false;
    this.showFeedback = false;
    this.quizScore = 0;
    this.quizPercentage = null;
    this.quizPassed = false;
    this.quizFeedback = [];
    this.wrongAnswerFeedback = null;
    this.passFeedback = null;
    this.currentQuestionIndex = 0;
    // KAN-478: a different quiz/placement — a pass on the previous one must not
    // leak in and suppress its terminal verdict (unlike retryQuiz, which is the
    // SAME quiz and deliberately carries it forward).
    this.hasPassedAttempt = false;

    this.hasManualGradingQuestions = false;
    this.manualGradingPending = 0;
    this.autoGradedScore = 0;
    this.autoGradedMaxScore = 0;
    this.submittedTotalPoints = null;
    this.isPartiallyGraded = false;

    // Reset smart retry state
    this.gradingStatus = null;
    this.guaranteedFail = false;
    this.manualPendingPoints = 0;
    this.bestPossiblePercentage = 0;

    this.isViewingPreviousSubmission = false;
    // KAN-627 R2: when the learner navigates from a prior-credit (or manually
    // granted) quiz to a different quiz/placement, the "Credited" flags must
    // reset so the fresh quiz's result block renders the earned score, not the
    // leaked credit banner on top of it. initializeEmptyQuiz already resets
    // these on the empty-fetch path; resetQuizState had been missing them.
    this.viewingManualGrant = false;
    this.viewingPriorCredit = false;
    this.submissionDate = null;
    this.showQuestionMismatchBanner = false;
    this.questionResults = null;
    this.attemptId = null;
    this.attemptLoadError = null;
    this.attemptStartFailed = false;
    this.attemptExpired = false;
    this.attemptsExhaustedAtStart = false;
    this.drawFailedBankId = null;
    this.attemptUpdatedNotice = false;
    this.moduleLocked = false;
    this.moduleLockedReason = null;
    this.questionsFromSnapshot = false;
    this.clearExpiryCountdown();
    this.attemptRemainingSeconds.set(null);

    this.checkForManualGradingQuestions();

    this.syncService.updateQuizState({
      totalQuestions: this.quizQuestions.length,
      isLocked: this.isVideoRequired && !this.videoCompleted
    });
  }
}
